import { React, useEffect, useState } from 'react';
import PageHeader from 'utility/PageHeader';
import PageBody from 'utility/PageBody';
import { Button, Form, Input, Row, Col, Select, Table, Tag, Modal } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { HttpHandler } from '../../shared/httpHandler';
import User from './User.js';
import PropTypes from 'prop-types';

const formDataEmpty = {
  id: null,
  account: '',
  name: '',
  department: '',
  reason: '',
  role_id: null,
  role_opts: [],
};

const UserMaintenance = () => {
  const httpHandler = new HttpHandler();
  const { Option } = Select;

  const [searchForm] = Form.useForm();
  const [userData, setUserData] = useState(formDataEmpty);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    updateData(pagination);
  };
  const updateData = (pagination = tableParams.pagination) => {
    const { account, name, approval } = searchForm.getFieldsValue();
    const { current, pageSize } = pagination;
    setLoading(true);
    httpHandler
      .get(
        `/user_maintenance/get_user_list?account=${account}&name=${name}&approval=${approval}&page=${current}&page_size=${pageSize}`
      )
      .then((response) => {
        if (response.success) {
          setTableData(response.data);
          setTableParams({
            ...tableParams,
            pagination: {
              ...pagination,
              total: response.total,
            },
          });
        } else {
          window.antdNotify('錯誤', response.data.message, 'fail');
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getRoleOptions = () => {
    httpHandler
      .get('/role_maintenance/get_role_list')
      .then((response) => {
        if (response.success) {
          const opts = response.data.map((item) => {
            return { label: item.name, value: item.id };
          });
          setUserData({ ...userData, role_opts: opts });
        } else {
          console.log(response.message);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const SearchBar = () => {
    const onFinish = async (values) => {
      setTableParams({
        ...tableParams,
        pagination: {
          current: 1,
          pageSize: 10,
        },
      });
      updateData({
        current: 1,
        pageSize: 10,
      });
    };
    return (
      <>
        <Form form={searchForm} name="searchForm" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="帳號" name="account">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="姓名" name="name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="權限狀態" name="approval">
                <Select>
                  <Option value="-1">全部</Option>
                  <Option value="1">通過</Option>
                  <Option value="0">未通過</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <User
                modeType="add"
                formData={userData}
                okFunc={updateData}
                okFuncProps={tableParams.pagination}
              />
            </Col>
            <Col span={6} className="button-group">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                htmlType="submit"
                className="button button--search"
                block>
                搜尋
              </Button>
            </Col>
          </Row>
        </Form>
      </>
    );
  };

  const DataTable = (props) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [user, setUser] = useState({});
    const confirmTitle = (
      <>
        <ExclamationCircleOutlined /> 變更審核狀態
      </>
    );
    const confirmContent = (
      <>
        確定將{' '}
        <b>
          {user.account} {user.name}
        </b>{' '}
        的權限狀態{' '}
        {user.approval === 0 ? (
          <Tag color="blue">開通</Tag>
        ) : (
          <Tag color="red">取消</Tag>
        )}
        嗎？
      </>
    );
    const confirmOk = async () => {
      setConfirmLoading(true);
      httpHandler
        .post('/user_maintenance/change_approval', {
          user_id: user.id,
          new_approval: user.approval === 0 ? 1 : 0,
        })
        .then((response) => {
          if (response.success) {
            window.antdNotify('修改成功！', '', 'success');
            setConfirmOpen(false);
            updateData();
          } else {
            window.antdNotify('錯誤', response.message, 'fail');
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setConfirmLoading(false);
        });
    };
    const confirmCancel = () => {
      setConfirmOpen(false);
    };
    const changeApproval = (user) => {
      setUser(user);
      setConfirmOpen(true);
    };
    let columns = [
      {
        title: 'id',
        dataIndex: 'id',
        hidden: true,
      },
      {
        title: '編輯',
        render: (_, row) => {
          return (
            <User
              modeType="edit"
              formData={{ ...row, role_opts: userData.role_opts }}
              okFunc={updateData}
              okFuncProps={tableParams.pagination}
            />
          );
        },
      },
      {
        title: '帳號',
        dataIndex: 'account',
      },
      {
        title: '姓名',
        dataIndex: 'name',
      },
      {
        title: '權限狀態',
        dataIndex: 'approval',
        render: (_, row) => {
          if (row.approval === 0) {
            return (
              <Button type="primary" onClick={() => changeApproval(row)}>
                開通
              </Button>
            );
          } else {
            return (
              <Button danger onClick={() => changeApproval(row)}>
                取消
              </Button>
            );
          }
        },
      },
      {
        title: '角色',
        dataIndex: 'role_name',
      },
      {
        title: '部門',
        dataIndex: 'department',
      },
      {
        title: '申請原因',
        dataIndex: 'reason',
      },
      {
        title: '有效開始日期',
        dataIndex: 'start',
      },
      {
        title: '有效結束日期',
        dataIndex: 'end',
      },
    ].filter((item) => !item.hidden);

    return (
      <>
        <Table
          columns={columns}
          rowKey={(row) => row.id}
          dataSource={props.tableData}
          pagination={tableParams.pagination}
          loading={loading}
          bordered="true"
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
        />
        <Modal
          title={confirmTitle}
          open={confirmOpen}
          onOk={confirmOk}
          confirmLoading={confirmLoading}
          onCancel={confirmCancel}>
          {confirmContent}
        </Modal>
      </>
    );
  };

  DataTable.propTypes = {
    tableData: PropTypes.array,
  };

  useEffect(() => {
    searchForm.setFieldsValue({
      account: '',
      name: '',
      approval: '-1',
    });
    getRoleOptions();
    updateData();
  }, [searchForm]);

  const section = [
    {
      hasTitle: false,
      title: 'Search',
      children: <SearchBar />,
    },
    {
      hasTitle: false,
      title: 'Data',
      children: <DataTable tableData={tableData} />,
    },
  ];

  return (
    <div>
      <PageHeader functionName={'user_maintenance'}></PageHeader>
      <PageBody section={section}></PageBody>
    </div>
  );
};

export default UserMaintenance;
